/**
 * URL resolution for HTTP (onRequest) Cloud Functions. Browser API calls should
 * use requestFunctionJson(), which applies the same-origin Hosting path and auth
 * header consistently. The direct helper remains available for non-browser use.
 *
 * Kept free of side effects (no initializeApp) so it can be imported and tested
 * without booting Firebase.
 */

const FUNCTIONS_REGION = 'us-central1';
const ADMIN_DATA_API_ROOTS = new Set(['settings', 'monk-planning', 'email-templates']);

/**
 * Pure precedence logic for the two things that need to resolve a Cloud Functions
 * base URL from env values: this module (browser runtime, via `import.meta.env`)
 * and vite.config.ts's dev-server proxy (Node/build time, via `loadEnv()`). Kept
 * as a plain function — no env access of its own — so both call it with their own
 * env source instead of each re-implementing the same override/emulator precedence.
 */
export function deriveFunctionsBaseUrl(params: {
  functionsUrlOverride?: string;
  projectId?: string;
  useEmulator?: string;
}): string {
  const { functionsUrlOverride, projectId, useEmulator } = params;
  if (functionsUrlOverride) return functionsUrlOverride.replace(/\/+$/, '');

  if (!projectId) {
    throw new Error(
      'Cannot resolve the Cloud Functions URL: set VITE_FIREBASE_FUNCTIONS_URL, or VITE_FIREBASE_PROJECT_ID to derive it.'
    );
  }

  return useEmulator === 'true'
    ? `http://127.0.0.1:5001/${projectId}/${FUNCTIONS_REGION}`
    : `https://${FUNCTIONS_REGION}-${projectId}.cloudfunctions.net`;
}

/** Same-origin URL routed by Firebase Hosting rewrites, e.g. /api/saveSheetSyncConfig. */
export function hostingFunctionPath(path: string): string {
  return `/api/${path.replace(/^\/+/, '')}`;
}

/**
 * Maps a same-origin browser API path to the direct Cloud Functions path used by
 * Vite's development proxy. Most routes map one-to-one to a function name, while
 * settings, monk planning, and email templates are resources grouped under the
 * adminData function.
 */
export function rewriteApiPathToFunction(url: string): string {
  const path = url.replace(/^\/api(?=\/|$)/, '') || '/';
  const resourceRoot = path.slice(1).split(/[/?]/, 1)[0];

  if (resourceRoot === 'donation-requests') {
    return `/donationRequests${path.slice('/donation-requests'.length)}`;
  }
  return ADMIN_DATA_API_ROOTS.has(resourceRoot) ? `/adminData${path}` : path;
}

/** Mount point for proxied images. Must match the `/img/**` rewrite in firebase.json. */
export const IMAGE_MOUNT = '/img';

/** The function firebase.json's `/img/**` rewrite targets. */
export const IMAGE_FUNCTION = 'serveImage';

/**
 * Maps a same-origin image path to the direct Cloud Functions path, for Vite's
 * development proxy.
 *
 * Deployed, `/img/**` is a Firebase Hosting rewrite; the dev server has no such
 * rewrite, so without this the path falls through to the SPA and every <img>
 * receives index.html. Lives here rather than inline in vite.config.ts for the
 * same reason rewriteApiPathToFunction does — so it can be imported and tested.
 * The lookahead is what keeps `/imgfoo` from being rewritten.
 */
export function rewriteImagePathToFunction(url: string): string {
  return url.replace(new RegExp(`^${IMAGE_MOUNT}(?=/|$)`), `/${IMAGE_FUNCTION}`);
}

/**
 * Headers for an admin-authenticated request to a hosting-routed function. Sent as
 * X-Firebase-Auth rather than Authorization — Firebase Hosting strips Authorization
 * on rewrites to onRequest functions, so hosting-routed calls need a different header
 * to carry the same bearer token (see SecurityService.bearerTokenFromRequest).
 */
export function firebaseAuthHeaders(idToken: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-Firebase-Auth': `Bearer ${idToken}`,
  };
}
